import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import type { UpdateStorageNodePayload } from '@/api/storageApi'
import { profileDisplayName, type KeptAtOption } from '@/lib/keptAt'
import { storageTemperaturesHref } from '@/lib/storageRoutes'
import { useUpdateStorageNode } from '@/hooks/useStorage'
import { useUndoToast } from '@/components/inventory/storage/UndoToast'
import { FAMILY_DOT, KeptAtBadge } from '@/components/inventory/storage/KeptAtBadge'
import { keptAtUndoPatch, type StorageListRow } from '@/lib/storageList'
import { cn } from '@/lib/utils'
import { InlineSelect } from './InlineSelect'

/** Exactly the controller's pinned copy for the stored-items warning. */
function storedItemsWarning(used: number): string {
  return used === 1
    ? "1 item stored here. If it can't be kept at the new temperature, the change is refused and nothing changes."
    : `${used} items stored here. If any can't be kept at the new temperature, the change is refused and nothing changes.`
}

/**
 * One row of the Kept-at menu: a family-coloured dot, the temperature in a
 * fixed-width column so every row lines up regardless of name length, then
 * the (deduped) display name in muted, truncated text. Deliberately not a
 * `KeptAtBadge` per option — a column of coloured bordered badges is what
 * read as a stack of separate pills; a plain row with one small dot does not.
 */
function KeptAtOptionRow({ option }: { option: KeptAtOption }) {
  return (
    <span className='flex min-w-0 flex-1 items-center gap-2'>
      <span
        className={cn('h-2 w-2 shrink-0 rounded-full', option.family ? FAMILY_DOT[option.family] : 'bg-muted-foreground')}
      />
      <span className='w-[8.5rem] shrink-0 tabular-nums font-medium'>{option.temperature}</span>
      <span className='min-w-0 flex-1 truncate text-muted-foreground'>{profileDisplayName(option.name)}</span>
    </span>
  )
}

/**
 * The list's "Kept at" cell — reads as the current condition, edits by picking
 * a Storage Profile (never a raw condition, per the global constraint).
 *
 * Purely controlled off `row`: nothing here is set optimistically, so a
 * refused change (Review Focus 2) simply never moves the displayed value —
 * `row.storageProfileId` only changes once the mutation succeeds and the
 * storage cache is invalidated. The error message is local state, shown under
 * the cell.
 *
 * The trigger is disabled while `update` is pending (readOnly, not hidden —
 * same look, no popover), so a second pick cannot fire before the first
 * settles and race past the previous value Undo captured.
 */
export function KeptAtCell({
  row,
  options,
  readOnly,
}: {
  row: StorageListRow
  options: KeptAtOption[]
  readOnly: boolean
}) {
  const update = useUpdateStorageNode()
  const showUndoToast = useUndoToast()
  const [error, setError] = useState<string | null>(null)

  const value = row.storageProfileId ?? ''
  const selectOptions = options.map((o) => ({ value: o.profileId, label: <KeptAtOptionRow option={o} /> }))

  /**
   * Both the edit and its Undo commit through here, so a refused Undo shows
   * the same inline error a refused edit would — never a silently dropped
   * change — and neither path sets anything optimistically.
   */
  async function commit(payload: UpdateStorageNodePayload, onCommitted?: () => void) {
    setError(null)
    try {
      await update.mutateAsync({ id: row.nodeId, ...payload })
      onCommitted?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change Kept at.')
    }
  }

  function handleChange(nextProfileId: string) {
    const option = options.find((o) => o.profileId === nextProfileId)
    if (!option) return
    // Captured from `row` as it stood before this change — including a legacy
    // direct condition, which `keptAtUndoPatch` is what keeps a plain
    // `{ storageProfileId: null }` undo from silently wiping (see its doc
    // comment: that payload clears both fields server-side).
    const undoPatch = keptAtUndoPatch(row)

    void commit({ storageProfileId: nextProfileId }, () => {
      showUndoToast(
        <>
          Kept at for <b>{row.code}</b> changed to {option.label}
        </>,
        () => void commit(undoPatch),
      )
    })
  }

  return (
    <div>
      <InlineSelect
        value={value}
        options={selectOptions}
        onChange={handleChange}
        readOnly={readOnly || update.isPending}
        renderValue={() => <KeptAtBadge condition={row.condition} />}
        ariaLabel={`Kept at for ${row.code}`}
        header={
          <>
            Kept at · {row.code}
            {row.used > 0 && (
              <div className='mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-amber-800'>
                <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                <span>{storedItemsWarning(row.used)}</span>
              </div>
            )}
          </>
        }
        footer={
          <Link
            to={storageTemperaturesHref('options')}
            className='block px-2 py-1.5 text-xs font-medium text-primary hover:underline'
            onClick={(e) => e.stopPropagation()}
          >
            Manage temperatures →
          </Link>
        }
      />
      {error && <p className='mt-1 text-xs text-destructive'>{error}</p>}
    </div>
  )
}
